import type { ILayerView } from "@esWebAPI/es/model/Layer";

export default class StylerFactory {
    buildStyler(layerView: ILayerView): JQueryDeferred<any> {
        return $.when(
            this.getThematicStyler(layerView, 'Color'),
            this.getThematicStyler(layerView, 'Size'),
            this.getThematicStyler(layerView, 'Symbol')
        ).then((colorThematicStyler, sizeThematicStyler, symbolThematicStyler) => {
            var baseStyler = this.getBaseStyler(layerView);
            var conditionalStyler = this.getConditionalStyler(layerView);
            var styler = this.getStyler(baseStyler, conditionalStyler, colorThematicStyler, sizeThematicStyler, symbolThematicStyler);
            
            return styler;
        });
    }
    
    private getThematicStyler(layerView: ILayerView, thematicType: string): JQueryDeferred<any> {
        var thematicSettings = layerView.styleSettings[thematicType.toLowerCase() + 'ThematicSettings'];
        if (!thematicSettings || !thematicSettings.enabled) {
            return es.util.deferred.resolved(null);
        }
        
        return this.getAttributeType(layerView.id, thematicSettings.attributeName).then((attributeType) => {
            if (thematicSettings.overrideThematic) {
                return es.util.deferred.resolved(attributeType, thematicSettings.overrideThematic);
            }
            
            return this.getThematicStylerThematic(layerView, thematicType).then((thematic) => {
                return es.util.deferred.resolved(attributeType, thematic);
            });
        }).then((attributeType, thematic) => {
            return {
                '$type': 'ThematicStyler',
                'thematic': thematic,
                'attributeType': attributeType,
                'defaultStyle': layerView.styleSettings.defaultStyle // TODO FIXME should not have a default style!
            };
        });
    }
    
    private getAttributeType(layerViewId: number, attribute) {
        return es.api.layerInfo.getForLayerView(layerViewId).then((layerInfo) => {
            return layerInfo.attributes.find((a) => a.name == attribute).attrType;
        });
    }

    private getThematicStylerThematic(layerView: ILayerView, thematicType: string): JQueryDeferred<any> {
        const thematicSettings = layerView.styleSettings[thematicType.toLowerCase() + 'ThematicSettings'];
        if (thematicSettings.overrideThematic) {
            return es.util.deferred.resolved(thematicSettings.overrideThematic);
        }
        
        return es.api.thematic['get' + thematicType + 'Thematic'](layerView.id, thematicSettings.attributeName);
    }


    private getBaseStyler(layerView: ILayerView) {
        const defaultStyle = this.getDefaultStyle(layerView.styleSettings);
        
        let baseStyler;
        if (layerView.styleSettings.colorPerFeatureSettings && layerView.styleSettings.colorPerFeatureSettings.enabled) {
            baseStyler = {
                '$type': 'ColorPerFeatureStyler',
                'defaultStyle': layerView.styleSettings.defaultStyle
            };
        }
        else {
            baseStyler = {
                '$type': 'SingleStyler',
                'style': layerView.styleSettings.defaultStyle
            };
        }
        
        return baseStyler;
    }

    private getDefaultStyle(styleSettings) {
        if (styleSettings.labelSettings.enabled) {
            return styleSettings.defaultStyle;
        }
        
        var style = es.util.object.deepCopy(styleSettings.defaultStyle);
        delete style.text;
        if (style.symbol) {
            delete style.symbol.text;
        }
        if (style.symbolStyle) {
            delete style.symbolStyle.text;
        }
        
        return style;
    }

    private getConditionalStyler(layerView: ILayerView) {
        if (!layerView.styleSettings.conditionalStyleSettings) {
            return null;
        }
        
        // settings has same props as old styler ("conditionalStyles", "name")
        const styler = es.util.object.deepCopy(layerView.styleSettings.conditionalStyleSettings);
        styler.$type = 'ConditionalStyler';
        
        return styler;
    }

    private getStyler(baseStyler, conditionalStyler, colorThematicStyler, sizeThematicStyler, symbolThematicStyler) {
        const stylers = [];
        if (conditionalStyler) stylers.push(conditionalStyler);
        if (colorThematicStyler) stylers.push(colorThematicStyler);
        if (sizeThematicStyler) stylers.push(sizeThematicStyler);
        if (symbolThematicStyler) stylers.push(symbolThematicStyler);
        
        if (stylers.length == 0) {
            return baseStyler;
        }
        
        return {
            '$type': 'CompoundStyler',
            'baseStyler': baseStyler,
            'stylers': stylers
        };
    }    
}
